
// src/pages/PeriyodikBakim.js
// Periyodik Bakım Takibi Sayfası - v4 Refactored

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, TextField,
    InputAdornment, Alert, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';

// Modül import'ları
import {
    useMaintenance,
    GeldiModal,
    SmsModal,
    formatDate,
    getDaysUntilMaintenance,
    getStatusChip
} from '../modules/maintenance';

import GlassCard from '../ui/glass/components/GlassCard';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import { Loading } from '../components/common/Loading';

const PeriyodikBakim = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleCount, setVisibleCount] = useState(25); // ekranda gösterilen satır sayısı (DOM'u hafifletir)

    // Modal state
    const [geldiModalOpen, setGeldiModalOpen] = useState(false);
    const [smsModalOpen, setSmsModalOpen] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(null);

    // Custom hook ile veri yönetimi
    const {
        devices,
        loading,
        actionLoading,
        handleGeldiConfirm,
        handleGelmedi,
        handleSendSms
    } = useMaintenance();

    // Arama filtresi — memoize edilir; her tuş/aksiyon sonrası 1000+ cihaz yeniden
    // filtrelenip tüm tablo yeniden render edilmesin diye (donma/kasma nedeni).
    const filteredDevices = useMemo(() => {
        const q = searchTerm.toLowerCase();
        if (!q) return devices;
        return devices.filter(device =>
            device.seriNumarasi?.toLowerCase().includes(q) ||
            device.musteriAdSoyad?.toLowerCase().includes(q) ||
            device.marka?.toLowerCase().includes(q)
        );
    }, [devices, searchTerm]);

    // Ekranda yalnızca ilk `visibleCount` cihazı göster; arama tüm veri üzerinde çalışır.
    const visibleDevices = filteredDevices.slice(0, visibleCount);

    // Müşteri geldi modal aç
    const openGeldiModal = (device) => {
        setSelectedDevice(device);
        setGeldiModalOpen(true);
    };

    // SMS modal aç
    const openSmsModal = (device) => {
        setSelectedDevice(device);
        setSmsModalOpen(true);
    };

    // Müşteri gelmedi işlemi
    const onGelmedi = async (device) => {
        if (!window.confirm(`${device.musteriAdSoyad} için 7 gün sonra otomatik SMS hatırlatması ayarlanacak.Onaylıyor musunuz ? `)) {
            return;
        }
        const result = await handleGelmedi(device);
        alert(result.message);
    };

    if (loading) {
        return <Loading message="Bakım kayıtları yükleniyor..." />;
    }

    return (
        <Box sx={{ p: { xs: 1, md: 1 } }}>
            {/* Header */}
            <PageHeader
                title="Periyodik Bakım Takibi"
                subtitle="Cihazların bakım durumlarını ve zamanlamalarını buradan takip edebilirsiniz."
                icon={<BuildCircleIcon />}
            >
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Seri No, Müşteri veya Marka Ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 300, bgcolor: 'background.paper', borderRadius: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
            </PageHeader>

            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Cihazlar bakım tarihine göre en yakından en uzağa sıralanmıştır. Kırmızı olanlar acil bakım gerektiren cihazlardır.
            </Alert>

            {/* Tablo */}
            <GlassCard sx={{ width: '100%', overflow: 'hidden', p: 0 }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Müşteri</TableCell>
                                <TableCell>Cihaz</TableCell>
                                <TableCell>Seri No</TableCell>
                                <TableCell align="center">Bakım Tarihi</TableCell>
                                <TableCell align="center">Durum</TableCell>
                                <TableCell align="center">Son Bakım</TableCell>
                                <TableCell align="center">İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleDevices.map((device) => {
                                const daysUntil = getDaysUntilMaintenance(device.periyodikBakimTarihi);
                                const isUrgent = daysUntil !== null && daysUntil <= 0;
                                const isLoading = actionLoading[device.id];

                                return (
                                    <TableRow
                                        key={device.id}
                                        hover
                                        sx={{
                                            bgcolor: isUrgent ? 'error.light' : 'inherit',
                                            opacity: isLoading ? 0.5 : 1,
                                        }}
                                    >
                                        <TableCell
                                            sx={{ cursor: 'pointer', fontWeight: 600, textDecoration: 'none !important' }}
                                            onClick={() => navigate(`/musteri/${device.musteriId}`)}
                                        >
                                            {device.musteriAdSoyad}
                                        </TableCell>
                                        <TableCell>{device.marka} {device.model}</TableCell>
                                        <TableCell
                                            sx={{ cursor: 'pointer', textDecoration: 'none !important' }}
                                            onClick={() => navigate(`/cihaz/${device.id}`)}
                                        >
                                            {device.seriNumarasi}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography fontWeight={isUrgent ? 'bold' : 'normal'}>
                                                {formatDate(device.periyodikBakimTarihi)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {getStatusChip(daysUntil, device.bakimDurumu)}
                                        </TableCell>
                                        <TableCell align="center">
                                            {formatDate(device.sonBakimTarihi)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                <Tooltip title="Müşteri Geldi - Bakım Yapıldı">
                                                    <span>
                                                        <Button
                                                            aria-label="Müşteri geldi - bakım yapıldı"
                                                            size="small"
                                                            variant="contained"
                                                            color="success"
                                                            onClick={() => openGeldiModal(device)}
                                                            disabled={isLoading}
                                                            sx={{ minWidth: 'auto', px: 1 }}
                                                        >
                                                            <CheckCircleIcon fontSize="small" />
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Müşteri Gelmedi - 7 Gün Sonra Hatırlat">
                                                    <span>
                                                        <Button
                                                            aria-label="Müşteri gelmedi - 7 gün sonra hatırlat"
                                                            size="small"
                                                            variant="contained"
                                                            color="error"
                                                            onClick={() => onGelmedi(device)}
                                                            disabled={isLoading}
                                                            sx={{ minWidth: 'auto', px: 1 }}
                                                        >
                                                            <CancelIcon fontSize="small" />
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Manuel SMS Gönder">
                                                    <span>
                                                        <Button
                                                            aria-label="Manuel SMS gönder"
                                                            size="small"
                                                            variant="contained"
                                                            color="info"
                                                            onClick={() => openSmsModal(device)}
                                                            disabled={isLoading}
                                                            sx={{ minWidth: 'auto', px: 1 }}
                                                        >
                                                            <NotificationsActiveIcon fontSize="small" />
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {filteredDevices.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ border: 0 }}>
                                        <EmptyState
                                            icon={<BuildCircleIcon />}
                                            message="Kayıt bulunamadı."
                                            description={searchTerm ? 'Aramanızla eşleşen cihaz yok.' : undefined}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </GlassCard>

            {visibleCount < filteredDevices.length && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">{visibleDevices.length} / {filteredDevices.length} gösteriliyor</Typography>
                    <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
                </Box>
            )}

            {/* Modaller */}
            <GeldiModal
                open={geldiModalOpen}
                onClose={() => setGeldiModalOpen(false)}
                device={selectedDevice}
                onConfirm={handleGeldiConfirm}
            />

            <SmsModal
                open={smsModalOpen}
                onClose={() => setSmsModalOpen(false)}
                device={selectedDevice}
                onSend={handleSendSms}
            />
        </Box>
    );
};

export default PeriyodikBakim;
